<template>
  <nav
    class="navbar"
    :class="{ 'two-row-navbar': showDropdowns }"
    role="navigation"
    aria-label="main navigation"
  >
    <!-- Row 1: Logo + Navigation Links -->
    <div class="container is-widescreen navbar-row-1 pb-0">
      <div class="navbar-brand" style="align-items: center">
        <div class="navbar-item">
          <div id="lab-info">
            <ncl-info-icon
              text="Click for more information on the lab."
              location="bottom"
            />
          </div>
          <h1
            class="title is-size-2-fullhd is-size-3-widescreen is-size-4-desktop is-size-4-touch"
          >
            The Nowcasting Lab
          </h1>
        </div>
        <a
          role="button"
          class="navbar-burger"
          aria-label="menu"
          aria-expanded="false"
          @click="onBurgerToggle"
          v-bind:class="{ 'is-active': isOpen }"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </a>
      </div>
      <div class="navbar-menu" v-bind:class="{ 'is-active': isOpen }">
        <div class="navbar-start">
          <router-link
            :to="{ name: 'Home' }"
            class="navbar-item"
            @click="resetToDefaults"
          >
            Home / Overview
          </router-link>
          <router-link
            :to="defaultDetailTo"
            class="navbar-item"
            @click="resetToDefaults"
          >
            Extended View
          </router-link>
          <router-link :to="{ name: 'About' }" class="navbar-item">
            About Nowcasting Lab
          </router-link>
          <router-link :to="{ name: 'News' }" class="navbar-item">
            News
          </router-link>
          <a
            href="https://blognowcastinglab.wordpress.com"
            @click="trackOutgoing"
            target="_blank"
            class="navbar-item"
          >
            Blog
          </a>
          <router-link :to="{ name: 'Partners' }" class="navbar-item">
            Partner Institutions
          </router-link>
          <router-link
            :to="{ name: 'Researchers-Developers' }"
            class="navbar-item"
          >
            R&D Team
          </router-link>
        </div>
      </div>
    </div>

    <!-- Row 2: Dropdowns and Controls (only show when showDropdowns is true) -->
    <div class="container is-widescreen navbar-row-2 p-0" v-if="showDropdowns">
      <div class="navbar-menu is-active">
        <div class="navbar-start">
          <div
            class="navbar-item has-dropdown is-hoverable"
            :class="{ 'is-active': isCountryDropdownOpen }"
          >
            <span
              class="navbar-link is-boxed"
              @click.stop="toggleCountryDropdown"
            >
              <ncl-flag :iso="countryStore.activeCountry.code" />
              &nbsp; {{ countryStore.activeCountry.name }}
            </span>
            <div class="navbar-dropdown is-boxed">
              <div
                class="navbar-item dropdown-option"
                v-for="(area, key, index) in countryStore.areas"
                :key="index"
                @click="onCountryChange(area)"
              >
                <ncl-flag :iso="area.code" />
                &nbsp; {{ area.name }}
              </div>
            </div>
          </div>

          <div
            class="navbar-item has-dropdown is-hoverable"
            :class="{ 'is-active': isPeriodDropdownOpen }"
          >
            <span class="navbar-link" @click.stop="togglePeriodDropdown">{{
              activeTargetPeriod
            }}</span>
            <div
              class="navbar-dropdown is-boxed"
              v-if="countryStore.activeIndicator.indicator === 'gdp'"
            >
              <div
                class="navbar-item dropdown-option"
                v-for="(targetPeriod, index) in countryStore.quarters"
                :key="index"
                @click="onTargetPeriodChange(targetPeriod.name)"
              >
                {{ targetPeriod.name }}
              </div>
            </div>
          </div>

          <div class="navbar-item open-extended-link">
            <detail-report-link :active-target-period="activeTargetPeriod">
              Open extended view in new tab
              <v-icon icon="mdi-open-in-new"></v-icon>
            </detail-report-link>
          </div>

          <div class="navbar-item gdp-forecast-item">
            <p>
              <span class="forecast-label"
                >GDP Forecast ({{ currentDate }}):</span
              >
              <span class="forecast-label-mobile">GDP:</span>
              <span
                class="has-text-weight-semibold"
                v-if="currentIndicatorValue"
                >{{ currentIndicatorValue }}%</span
              >
              <span class="has-text-weight-semibold" v-else>No data</span>
            </p>
          </div>

          <div class="navbar-item trend-item">
            <p>
              <span class="trend-label">7-Day Trend: &nbsp;</span>
              <v-icon v-if="currentTrend > 0" icon="mdi-trending-up"></v-icon>
              <v-icon
                v-if="currentTrend === 0"
                icon="mdi-trending-neutral"
              ></v-icon>
              <v-icon
                v-else-if="currentTrend < 0"
                icon="mdi-trending-down"
              ></v-icon>
              <v-icon v-else-if="!currentTrend" icon="mdi-minus"></v-icon>
            </p>
          </div>

          <router-link
            :to="{ name: 'Data Download' }"
            class="navbar-item data-download-link"
            aria-label="Data Download"
          >
            <span class="data-download-text">Data Download</span>
            <v-icon class="data-download-icon" icon="mdi-download"></v-icon>
          </router-link>
        </div>
      </div>
    </div>
    <!-- Lab Info Modal -->
    <v-dialog activator="#lab-info">
      <template v-slot="{ isActive }">
        <ncl-lab-modal @close="isActive.value = false" />
      </template>
    </v-dialog>
  </nav>
</template>

<script>
import { usePlausible } from "v-plausible/vue";
import DetailReportLink from "@/components/DetailReportLink.vue";
import NclFlag from "@/components/NclFlag.vue";
import NclInfoIcon from "@/components/NclInfoIcon.vue";
import NclLabModal from "@/components/NclLabModal.vue";
import { mapStores } from "pinia";
import { useCountryStore } from "@/stores/country";

export default {
  name: "NclHeader",
  props: {
    showDropdowns: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { trackEvent, trackPageview } = usePlausible();
    return { trackEvent, trackPageview };
  },
  components: {
    DetailReportLink,
    NclFlag,
    NclInfoIcon,
    NclLabModal,
  },
  computed: {
    ...mapStores(useCountryStore),
    currentIndicatorValue() {
      if (!this.showDropdowns) return "";

      const val = this.countryStore.getIndicatorValueFromCountry(
        this.countryStore.activeCountry.code,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );
      if (val) {
        const indicatorValue = val.toFixed(2);
        return indicatorValue > 0
          ? `+${indicatorValue}`
          : indicatorValue.toString();
      }
      return "";
    },
    currentDate() {
      if (!this.showDropdowns) return "";

      const options = { year: "numeric", month: "short", day: "numeric" };
      const data = this.countryStore.getIndicatorTPHistory(
        this.countryStore.activeCountry,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );
      if (data.length > 0) {
        const date = new Date(data[data.length - 1][0]);
        return date.toLocaleDateString("en-US", options);
      }
      return "";
    },
    currentTrend() {
      if (!this.showDropdowns) return 0;

      const data = this.countryStore.getIndicatorTPHistory(
        this.countryStore.activeCountry,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );
      const forecastWindow = 8;
      let latestForecast = 0;
      let oldestForecast = 0;
      if (data.length > 0) {
        latestForecast = data[data.length - 1][1];
        oldestForecast =
          data.length < forecastWindow
            ? data[0][1]
            : data[data.length - forecastWindow][1];
      }
      return latestForecast - oldestForecast;
    },
    defaultDetailTo() {
      return {
        name: "Detail",
        query: {
          country: "ch",
          targetvariable: "gdp",
          targetperiod:
            this.countryStore.quarters.length > 1
              ? this.countryStore.quarters[1].name
              : "",
        },
      };
    },
    activeTargetPeriod: {
      get() {
        if (this.countryStore.selectedTargetPeriod) {
          return this.countryStore.selectedTargetPeriod;
        }
        if (this.countryStore.quarters.length) {
          return this.countryStore.quarters[1].name;
        }
        return "";
      },
      set(newPeriod) {
        this.countryStore.selectedTargetPeriod = newPeriod;
        this.$emit("targetPeriodChanged", newPeriod);
      },
    },
  },
  methods: {
    resetToDefaults() {
      this.countryStore.activeCountry = {
        name: "Switzerland",
        code: "ch",
        adjective: "Swiss",
        countries: ["CH"],
      };
      this.countryStore.selectedTargetPeriod = null;
    },
    trackOutgoing(e) {
      this.trackEvent("Outbound Link: Click", {
        props: { url: e.target.href },
      });
      e.currentTarget.blur();
    },
    onCountryChange(area) {
      this.isCountryDropdownOpen = false;
      this.countryStore.activeCountry = area;

      // Update URL on Detail page
      if (this.$route.name === "Detail") {
        this.$router.push({
          name: "Detail",
          query: {
            ...this.$route.query,
            country: area.code,
          },
        });
      }
    },
    onTargetPeriodChange(periodName) {
      this.isPeriodDropdownOpen = false;
      this.countryStore.selectedTargetPeriod = periodName;
      this.$emit("targetPeriodChanged", periodName);

      // Update URL on Detail page
      if (this.$route.name === "Detail") {
        this.$router.push({
          name: "Detail",
          query: {
            ...this.$route.query,
            targetperiod: periodName,
          },
        });
      }
    },
    closeDropdowns() {
      this.isCountryDropdownOpen = false;
      this.isPeriodDropdownOpen = false;
    },
    toggleCountryDropdown() {
      this.isPeriodDropdownOpen = false;
      this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
    },
    togglePeriodDropdown() {
      this.isCountryDropdownOpen = false;
      this.isPeriodDropdownOpen = !this.isPeriodDropdownOpen;
    },
    onBurgerToggle() {
      this.isOpen = !this.isOpen;
      this.closeDropdowns();
    },
    initializeFromRoute() {
      // Initialize dropdowns from route params when on Detail page
      if (this.$route.name === "Detail" && this.$route.query) {
        const { country, targetperiod } = this.$route.query;

        if (country) {
          const countryObj = this.countryStore.areas.find(
            (area) => area.code === country,
          );
          if (countryObj && this.countryStore.activeCountry.code !== country) {
            this.countryStore.activeCountry = countryObj;
          }
        }

        if (
          targetperiod &&
          targetperiod !== this.countryStore.selectedTargetPeriod
        ) {
          this.countryStore.selectedTargetPeriod = targetperiod;
        }
      }
    },
  },
  async mounted() {
    // Ensure quarters are loaded before initializing
    if (this.countryStore.quarters.length === 0) {
      await this.countryStore.loadQuarters();
    }
    this.initializeFromRoute();
    document.addEventListener("click", this.closeDropdowns);
  },
  unmounted() {
    document.removeEventListener("click", this.closeDropdowns);
  },
  watch: {
    $route() {
      this.isOpen = false;
    },
    "$route.query": {
      handler() {
        this.initializeFromRoute();
      },
      deep: true,
    },
  },
  data() {
    return {
      isOpen: false,
      isCountryDropdownOpen: false,
      isPeriodDropdownOpen: false,
    };
  },
};
</script>

<style scoped lang="scss">
@import url("bulma/css/bulma.min.css");

.two-row-navbar {
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  overflow: visible;
}

// Sticky instead of Bulma's is-fixed-top: stays in flow, so content below
// never needs hardcoded margin-top offsets — robust to wrapping and zoom.
nav.navbar {
  position: sticky;
  top: 0;
  z-index: 30;
  overflow: visible;
}

.navbar-row-1,
.navbar-row-2 {
  overflow: visible;
}

.navbar-row-2 .navbar-menu,
.navbar-row-2 .navbar-start {
  overflow: visible;
}

.navbar .navbar-menu {
  overflow: visible !important;
}

.navbar-row-1 {
  border-bottom: 1px solid;
  border-bottom-color: #e8e8e8;
  padding-bottom: 0.5rem;
}

.navbar-row-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  .forecast-label-mobile {
    display: none;
  }

  .navbar-menu {
    box-shadow: none;
    padding: 0;

    // Always show on desktop
    @media screen and (min-width: 1024px) {
      display: flex !important;
    }
  }

  .navbar-start {
    flex-wrap: wrap;
  }

  // Mobile: collapse dropdowns — only show when is-active on parent
  @media screen and (max-width: 1023px) {
    padding-top: 0;
    padding-bottom: 0;

    .navbar-start {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }

    .navbar-item {
      padding: 0.25rem 0.5rem;
    }

    .open-extended-link {
      display: none;
    }

    .forecast-label {
      display: none;
    }

    .forecast-label-mobile {
      display: inline;
    }

    .data-download-text {
      display: none;
    }

    .navbar-item.has-dropdown {
      padding: 0;
      position: relative;

      .navbar-link {
        padding: 0.5rem 2.5em 0.5rem 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.3rem;
        width: auto;
        min-width: 0;
        cursor: pointer;
      }

      .navbar-dropdown,
      .navbar-dropdown.is-boxed {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 30;
        min-width: 11rem;
        max-width: calc(100vw - 1rem);
        background-color: #ffffff !important;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
        border-radius: 0;
        padding: 0.25rem 0 !important;

        .navbar-item {
          background-color: #ffffff !important;
          color: #333333 !important;
          padding: 0.5rem 0.75rem !important;
          display: flex !important;
          align-items: center;
        }
      }

      &.is-active {
        z-index: 40;
        .navbar-dropdown {
          display: block;
        }
      }
    }
  }

  // Phone-only: split dropdowns onto own row, full width below trigger
  @media screen and (max-width: 480px) {
    .navbar-item.has-dropdown {
      flex: 1 1 50%;

      .navbar-link {
        width: 100%;
        justify-content: flex-start;
      }

      .navbar-dropdown {
        left: 0;
        right: 0;
        min-width: 0;
        width: 100%;
      }
    }
  }
}

// 1024–1407: Bulma gives .navbar-menu flex-shrink: 0 and caps the container
// at 1152px (widescreen) or less, but the full rows need ~1210px — they
// overflow and get clipped. Tighten paddings/min-widths so each row fits.
@media screen and (min-width: 1024px) and (max-width: 1407px) {
  // Bulma pulls these -0.75rem outside the container, off-screen when it's full width
  .navbar > .container.navbar-row-1 .navbar-brand {
    margin-left: 0;
  }

  .navbar > .container .navbar-menu {
    margin-right: 0;
  }

  .navbar-row-1 .navbar-item {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
  }

  .navbar-row-2 {
    .navbar-item {
      padding-left: 0.375rem;
      padding-right: 0.375rem;
    }

    .navbar-item.has-dropdown .navbar-link {
      min-width: 130px;
    }
  }
}

// Bottom of the desktop range (1024px iPads): still too tight — shrink type
// and reuse the mobile short labels/icons.
@media screen and (min-width: 1024px) and (max-width: 1139px) {
  .navbar-row-1 .title {
    font-size: 1.25rem !important; // beat Bulma's .is-size-4-desktop !important
  }

  .navbar-row-1 .navbar-menu .navbar-item {
    font-size: 0.875rem;
  }

  .navbar-row-2 {
    .forecast-label,
    .data-download-text {
      display: none;
    }

    .forecast-label-mobile {
      display: inline;
    }
  }

  .data-download-icon {
    display: inline-flex !important;
  }
}

.data-download-icon {
  display: none;
}

@media screen and (max-width: 1023px) {
  .data-download-icon {
    display: inline-flex;
  }
}

// Desktop hover behavior for is-boxed dropdowns
@media (min-width: 1024px) {
  .navbar-item.is-hoverable:hover .navbar-dropdown {
    display: block;
  }

  .navbar-item.is-hoverable:hover .navbar-dropdown.is-boxed {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
}

.navbar-item.dropdown-option {
  cursor: pointer;
}

.navbar-link:hover {
  color: rgb(124, 181, 236);
}

.navbar-item a:hover {
  color: rgb(124, 181, 236);
}

.navbar-item.has-dropdown .navbar-link {
  min-width: 200px;
}

.navbar-dropdown {
  max-height: 60vh;
  overflow-y: auto;
}
</style>

<style lang="scss">
// Dark theme navbar styles (non-scoped for body selector to work)
body.dark-theme {
  // Navbar background and border
  nav.navbar {
    background-color: #1a1a1a;
    border-bottom-color: #404043;
  }

  .navbar-row-1 {
    border-bottom-color: #404043;
  }

  // Burger icon lines
  .navbar-burger span {
    background-color: #e0e0e0;
  }

  // Mobile expanded menu sheet
  .navbar-menu {
    background-color: #2d2d2d;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);

    .navbar-item {
      color: #e0e0e0;

      &:hover,
      &:focus {
        background-color: #404043;
        color: #e0e0e0;
      }
    }

    // Active router-link highlight
    a.navbar-item.router-link-active,
    a.navbar-item.router-link-exact-active {
      color: #7cb5ec;
    }
  }

  // Dropdown trigger link
  .navbar-link {
    background-color: #2d2d2d;
    color: #e0e0e0;
  }

  // Dropdown panel
  .navbar-dropdown,
  .navbar-dropdown.is-boxed {
    background-color: #2d2d2d !important;
    border-color: #404043;

    .navbar-item {
      background-color: #2d2d2d !important;
      color: #e0e0e0;
    }
  }

  .navbar-item.dropdown-option:hover {
    background-color: #404043 !important;
  }
}

// Light theme navbar styles
body.light-theme {
  nav.navbar {
    background-color: #ffffff;
  }

  .navbar-burger span {
    background-color: #333333;
  }

  .navbar-menu {
    background-color: #ffffff;

    .navbar-item {
      color: #333333;

      &:hover,
      &:focus {
        background-color: #f5f5f5;
        color: #333333;
      }
    }
  }

  .navbar-link {
    background-color: #ffffff;
    color: #333333;
  }

  .navbar-dropdown {
    background-color: #ffffff;
    border-color: #dbdbdb;

    .navbar-item {
      background-color: #ffffff;
      color: #333333;
    }
  }

  .navbar-item.dropdown-option:hover {
    background-color: #eee;
  }
}
</style>
